import { z } from "zod";
import { DateTime } from "luxon";
import {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueHandicapMethod,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueWizardScheduleMode,
  GimmeDistance,
  GroundConditions,
  HandicapMode,
  HandicapPercentage,
  PinType,
  ScoringType,
  SkyConditions,
  TeeType,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "../enums";
import { LeagueCompetitionType } from "@/types/enums";
import { utcTodayIso } from "./helpers";

const courseSettingsSchema = z.object({
  selectedHoles: z.array(z.number().int()).optional(),
  autoConcede: z.enum(AutoConcede).optional(),
  autoContinue: z.enum(AutoContinue).optional(),
  autoDrop: z.boolean().optional(),
  autoGimmes: z.enum(AutoGimme).optional(),
  gimmeDistance: z.enum(GimmeDistance).optional(),
  handicapMode: z.enum(HandicapMode).optional(),
  handicapPercentage: z.enum(HandicapPercentage).optional(),
  courseRatingSlope: z.boolean().optional(),
  mulligans: z.boolean().optional(),
  pinType: z.enum(PinType).optional(),
  scoringType: z.enum(ScoringType).optional(),
  windSpeed: z.enum(WindSpeed).optional(),
  stimpSetting: z.number().optional(),
  mensTee: z.enum(TeeType).optional(),
  womensTee: z.enum(TeeType).optional(),
  groundConditions: z.enum(GroundConditions).optional(),
  windDirection: z.enum(WindDirection).optional(),
  skyConditions: z.enum(SkyConditions).optional(),
  timeConditions: z.enum(TimeOfDay).optional(),
});

/**
 * The round's own fields, before the scheduling-mode rules below.
 *
 * Exported so the round editor can validate a Season Flex round against it:
 * the round-detail endpoint returns no `dueByUtc`, and a flex round has no
 * play window, so the "exactly one mode" rule can never be satisfied from that
 * payload — it would block every edit to a flex round, including ones that
 * never touched a date. Drop the field-level rules here and the mode rules
 * stay in force everywhere a mode is actually choosable.
 */
export const roundItemFieldsSchema = z.object({
  // Server-assigned identifier (string) for an existing round. Present only
  // when editing; this is the value the PATCH endpoint keys off of, and is
  // distinct from the human-facing roundNumber.
  roundId: z.string().optional(),
  roundNumber: z.number().int(),
  name: z.string(),
  // A round's competition type is always the league's scoring type (locked in
  // the UI); it is never a per-round choice.
  competitionType: z
    .enum(Clubhouse_LeagueScoring, {
      message: "Select a competition type",
    })
    .optional(),
  // Fixed-time rounds have a play window; Flex rounds have only a due-by date
  // (no window). Both are optional here; the refine below requires one mode.
  playWindowStartUtc: z.string().optional(),
  playWindowEndUtc: z.string().optional(),
  dueByUtc: z.string().optional(),
  // Coerce: the course picker is a native <select>, whose value is a string
  // (a pre-filled value that's never re-selected stays a string). Matches the
  // z.coerce.number() pattern used by the other numeric selects here.
  courseId: z.coerce.number().int().min(1, "Course is required"),
  settings: courseSettingsSchema.optional(),
});

export const seasonRoundItemSchema = roundItemFieldsSchema
  // Exactly one scheduling mode — a complete play window (Fixed-time) OR a
  // due-by (Flex), never both and never a partial window. This mirrors the wire
  // contract (Clubhouse_PostLeagueV2Round) so a Flex round can't silently become
  // a windowed round.
  .refine(
    (d) => {
      const hasWindow = !!d.playWindowStartUtc && !!d.playWindowEndUtc;
      const noWindow = !d.playWindowStartUtc && !d.playWindowEndUtc;
      const hasDueBy = !!d.dueByUtc;
      return (hasWindow && !hasDueBy) || (hasDueBy && noWindow);
    },
    {
      message: "A round needs either a play window or a due date, not both",
      path: ["playWindowStartUtc"],
    },
  )
  .refine(
    (d) => {
      if (!d.playWindowStartUtc || !d.playWindowEndUtc) return true;
      const startDate = DateTime.fromISO(d.playWindowStartUtc.slice(0, 10), {
        zone: "utc",
      });
      const endDate = DateTime.fromISO(d.playWindowEndUtc.slice(0, 10), {
        zone: "utc",
      });
      return (
        startDate.isValid &&
        endDate.isValid &&
        endDate.toMillis() >= startDate.toMillis()
      );
    },
    {
      message: "End date must be on or after start date",
      path: ["playWindowEndUtc"],
    },
  );

export type SeasonRoundItem = z.infer<typeof seasonRoundItemSchema>;

export const leagueFormSchema = z
  .object({
    facilityScope: z.enum(Clubhouse_LeagueFacilityScope, {
      message: "Select a facility scope",
    }),
    facilityId: z.string().min(1, "Facility ID is required"),
    // The facilities a multi-facility league is played at (Location step).
    participatingClubhouseFacilityIds: z.array(z.number().int()).optional(),
    name: z.string().min(1, "League name is required").max(100),
    description: z
      .string()
      .min(1, "Description is required")
      .max(500, "Description must be less than 500 characters"),
    leagueStartDate: z.string().min(1, "Start date is required"),
    leagueEndDate: z.string().min(1, "End date is required"),
    leagueStartDateTimezone: z.string().optional(),
    leagueEndDateTimezone: z.string().optional(),
    competitionType: z
      .array(
        z.object({
          competitionType: z.enum(LeagueCompetitionType, {
            message: "Select a competition type",
          }),
          name: z.string().optional(),
          id: z.number().int().optional(),
        }),
      )
      .optional(),
    settings: z
      .object({
        leagueType: z.enum(Clubhouse_LeagueType, {
          message: "Select a league type",
        }),
        defaultGroupSize: z.coerce.number().int().min(1).max(4).optional(),
        participantCap: z.coerce
          .number()
          .int()
          .min(1, "Participant cap is required")
          .max(150, "Maximum is 150"),
        handicapMethod: z.enum(Clubhouse_LeagueHandicapMethod, {
          message: "Select a handicap method",
        }),
        handicapMode: z.enum(HandicapMode, {
          message: "Select a handicap mode",
        }),
        handicapPercentage: z.enum(HandicapPercentage, {
          message: "Select a handicap percentage",
        }),
        courseRatingSlope: z.boolean({
          message: "Select a course rating slope",
        }),
        leagueScoringType: z.enum(Clubhouse_LeagueScoring, {
          message: "Select a league scoring type",
        }),
        leagueTeamSize: z.coerce.number().int().min(1).max(4).optional(),
      })
      .optional(),
    // Guided Setup's own answers. No participant cap: Guided no longer asks for
    // one and a league created from it has no limit.
    wizard: z.object({
      leagueType: z.enum(Clubhouse_LeagueType, {
        message: "Select a league type",
      }),
      leagueScoringType: z.enum(Clubhouse_LeagueScoring, {
        message: "Select a league scoring type",
      }),
      teamSize: z.coerce.number().int().min(1).max(4).optional(),
      scheduleMode: z.enum(Clubhouse_LeagueWizardScheduleMode, {
        message: "Select a schedule mode",
      }),
    }),
    seasonRounds: z.array(seasonRoundItemSchema).optional(),
    seasonRoundCount: z.coerce
      .number()
      .int()
      .min(1, "Add at least one round before continuing")
      .max(52),
  })
  // The backend requires the end date to be at least one calendar day after
  // the start (a same-day season returns 400), so this is a strict `<`.
  .refine((d) => new Date(d.leagueStartDate) < new Date(d.leagueEndDate), {
    message: "End date must be at least one day after the start date",
    // Path must be a rendered field so the error shows (and is caught by the
    // wizard's step-field re-validation). "endDateUtc" matched no field/step,
    // so this error was silently unreachable.
    path: ["leagueEndDate"],
  })
  // Mirror the API's UTC-day rule (BeInFutureDate): the start date can't be
  // before today in UTC. Compared as yyyy-MM-dd strings (lexicographic order
  // matches date order) so it doesn't drift with the local clock.
  .refine((d) => !d.leagueStartDate || d.leagueStartDate >= utcTodayIso(), {
    message: "Start date can’t be before today.",
    path: ["leagueStartDate"],
  });

export type LeagueFormValues = z.infer<typeof leagueFormSchema>;
